import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // Hardcoded admin email — route straight to the admin workspace on login.
  // Admin credentials are never surfaced in the UI; only the owner knows them.
  const ADMIN_EMAIL = 'silas.phiri21@gmail.com';

  function routeAfterLogin(targetEmail) {
    if (targetEmail && targetEmail.trim().toLowerCase() === ADMIN_EMAIL) {
      nav('/admin');
      return;
    }
    nav('/dashboard');
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
      routeAfterLogin(email);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  async function onGoogle() {
    setErr(null);
    setGoogleBusy(true);
    try {
      const result = await loginWithGoogle();
      const emailFromGoogle = result?.user?.email || '';
      routeAfterLogin(emailFromGoogle);
    } catch (e) {
      // Popup closed by user → ignore; anything else → surface
      const code = e && e.code ? String(e.code) : '';
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setErr(e instanceof Error ? e.message : 'Google sign-in failed');
      }
    } finally {
      setGoogleBusy(false);
    }
  }

  return (
    <div className="auth-wrapper">
      <aside className="auth-panel">
        <div>
          <Link to="/" className="d-inline-flex align-items-center gap-2 text-decoration-none" style={{ color: '#fff' }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                display: 'grid',
                placeItems: 'center',
                fontSize: '1rem',
                boxShadow: 'var(--shadow-brand)',
              }}
            >
              <i className="fas fa-graduation-cap"></i>
            </span>
            <span style={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Dissertation Island</span>
          </Link>
        </div>
        <div>
          <h2 style={{ fontWeight: 800, fontSize: '2.2rem', lineHeight: 1.15, color: '#fff' }}>
            Welcome back.
            <br />
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: 'italic',
                fontWeight: 400,
                background: 'linear-gradient(135deg, #818cf8, #38bdf8)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Let's keep writing.
            </span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: 420, marginTop: '1rem' }}>
            Pick up where you left off — your drafts, credit balance and downloads are all waiting
            in your dashboard.
          </p>
        </div>
        <div
          className="d-flex align-items-center gap-3 small"
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          <i className="fas fa-lock"></i>
          Secure login with 256-bit encryption
        </div>
      </aside>

      <div className="auth-form-side">
        <div className="auth-card fade-in-up">
          <div className="auth-icon">
            <i className="fas fa-sign-in-alt"></i>
          </div>
          <div className="text-center mb-4">
            <h3 className="fw-bold mb-1">Log in to your account</h3>
            <p className="text-muted small mb-0">
              New here?{' '}
              <Link to="/register" className="fw-semibold">
                Create an account
              </Link>
            </p>
          </div>

          {err && <div className="alert alert-danger">{err}</div>}

          <button
            type="button"
            className="btn btn-outline-secondary w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
            onClick={onGoogle}
            disabled={googleBusy || busy}
            style={{ padding: '0.6rem', fontWeight: 500 }}
          >
            {googleBusy ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" />
                <span>Opening Google…</span>
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                <span>Continue with Google</span>
              </>
            )}
          </button>

          <div className="d-flex align-items-center gap-3 my-3 text-muted small">
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span>or with email</span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <form onSubmit={onSubmit} autoComplete="off">
            {/* Decoy fields — Chrome/Safari autofill these instead of the
                real inputs below, so a previously-saved credential (e.g.
                sp@gmail.com) can't clobber whatever the user is typing. */}
            <input
              type="text"
              name="fake-username"
              autoComplete="username"
              style={{ display: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <input
              type="password"
              name="fake-password"
              autoComplete="current-password"
              style={{ display: 'none' }}
              tabIndex={-1}
              aria-hidden="true"
            />
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="di_email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
                autoComplete="off"
              />
            </div>
            <div className="mb-4">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="di_password"
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn btn-cta w-100 py-2" disabled={busy}>
              {busy ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Signing in…
                </>
              ) : (
                <>
                  Log in
                  <i className="fas fa-arrow-right ms-2"></i>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
